import {
  notification,
  Button,
  Col,
  Form,
  Row,
  Input,
  Space,
  Upload,
  UploadFile,
  UploadProps,
  Card,
  Checkbox,
  Mentions,
  Popover,
  Select,
  SelectProps,
} from "antd";
import { useContext, useEffect, useRef, useState } from "react";
import { ConfiguratorContext, S3UrlMapContext } from "../context";
import { useForm } from "antd/lib/form/Form";
import {Comment, CommentTopic, NewComment, User} from "../api/models"
import _ from "lodash";
import { useIntl } from "react-intl";
import { AsyncState, useAsyncState } from "../hook/useAsyncState";
import {  DownloadOutlined, PlusOutlined, EditFilled } from "@ant-design/icons";
import dayjs from "dayjs";
import useUsers from "../swr/useUsers";
import {AxiosResponse} from "axios";

export function asComment(comment: NewComment | Comment | undefined) : Comment | undefined {
  return !isNewComment(comment) ? comment as Comment : undefined;
}
export function isNewComment(comment: Comment | NewComment | undefined) : boolean {
  if ( !comment ) return false;
  return !('id' in comment) || comment.id === undefined;
}

interface UploadedDocumentAsset extends UploadFile {
  id?  : number
  assetUri: string
}
const CommentCard = (props: {
  comment:Comment
  onEdit: (id:number) => void
}) => {

  const configurator = useContext(ConfiguratorContext);
  const { comment } = props;
  const handleDownloadDocument = (file:UploadFile) => {
    const document = file.response || file;
    if ( document.url ) {
      configurator.api.downloadFile( document.url, {
        mimeType: document.type,
        filename: document.name
      })
    }
  }

  const handleEditComment = () => {
    props.onEdit( comment.id );
  }
  const { s3UrlMap } = useContext(S3UrlMapContext);

  const canEdit = ( comment.createdBy?.id === configurator.userInfo?.id || configurator.isAdmin() ) && comment.topic !== CommentTopic.SystemActivity;

  const documents = comment.documents?.map( d => ({
    uid: d.assetUri,
    id: d.id,
    assetUri: d.assetUri,
    name: d.filename,
    type: d.contentType,
    status: 'done',
    downloadUrl: s3UrlMap?.[ d.assetUri ],
    url: s3UrlMap?.[ d.assetUri ],
  })) as UploadFile[];

  const backgroundColor = comment.hidden ? "#f9f9f9" : undefined;
  
  const hasEdit = comment.createdAt !== comment.updatedAt || comment.createdBy?.id !== comment.updatedBy?.id;

  return <Card
    styles={{
      header: { minHeight: "auto", padding: ".4rem .4rem .4rem .4rem" },
      body:{padding: ".4rem", backgroundColor }
    }}
    title={<>
      <Row justify={"space-between"}>
        <Col style={{overflowX: "clip", maxWidth: "18rem", fontWeight:"500"}}>
          {[comment.createdBy?.name, dayjs(comment.createdAt).format('MM/DD/YYYY h:mm a')].filter(v=>v).join(" - " )}
        </Col>
        {canEdit &&
        <Col><Button size="small" onClick={handleEditComment} shape="circle" icon={<EditFilled />} /></Col>
        }
      </Row>
    </>}
  >
    {!!comment.text?.length &&
    <div>{comment.text}</div>
    }

    {!!comment.tags?.length && 
    <div style={{display: "flex", flexDirection: "row", marginTop: ".4rem", gap: "2px"}}>
      {comment.tags.map( t => <div key={t} ><span style={{fontStyle: "italic", fontSize: "smaller", }}>{t}</span></div>)}
    </div>
    }

    {!!documents?.length &&
      <div style={{marginTop: ".2rem"}} >
        <Upload
          fileList={documents}
          listType="picture-card"
          showUploadList={ {
            showDownloadIcon: true,
            downloadIcon: <DownloadOutlined />,
            showPreviewIcon: true,
            showRemoveIcon: false,
          }}
          onDownload={handleDownloadDocument}
          isImageUrl={(f) => !!f.type?.startsWith("image")  }
        />
      </div>
    }

    {(hasEdit || comment.internal ) && 
      <Row style={{marginTop: ".2rem"}} justify={"space-between"} gutter={8}>
        <Col>
          {hasEdit &&
          <span style={{fontStyle: "italic", fontSize: "smaller", }}>
            edit: {[comment.updatedBy?.name, dayjs(comment.updatedAt).format('MM/DD/YYYY h:mm a')].filter(v=>v).join(" - " )}
          </span>
          }
        </Col>
        <Col>
          {comment.internal &&
          <span style={{fontStyle: "italic", fontSize: "smaller", }}>
            internal
          </span>
          }
        </Col>
      </Row>
    }
          
  </Card>;

}




type CommentForm = Omit<Comment, "documents"> & {
  documents?: UploadFile[] 
}
export const EditCommentCard = (props: {
  reset?:boolean
  comment?:Comment | NewComment
  topicTags?: string[] | undefined
  onCancel:() => void
  onSave:(c:Comment) => void
  createApi: (comment: NewComment) => Promise<AxiosResponse<Comment> | undefined>
  updateApi: (comment: Comment) => Promise<AxiosResponse<Comment> | undefined>
}) => {

  const configurator = useContext(ConfiguratorContext);
  const intl = useIntl();
  const [form] = useForm();
  const [_comment, commentAsync] = useAsyncState<Comment>();

  const users = useUsers().data;

  useEffect(() => {
    form.resetFields();
  }, [props.reset, (props.comment as Comment )?.id] );

  const mentionUserMap = users?.map( u => ({ [_.upperFirst(_.camelCase( u.name))]: u })).reduce( (acc, u) => ({...acc, ...u}), {} );

  const parseUserFromMentions = (text:string | undefined) : User[] | undefined => text?.match(/@[a-zA-Z0-9]+/g)
    ?.map( m => m.substring(1) )
    .map( m => mentionUserMap?.[m] ).filter(v => v) as User[] | undefined;

  const createComment = async (commentAsync:AsyncState<Comment>, comment:NewComment) : Promise<Comment | undefined> => {

    const mentions = parseUserFromMentions(comment.text)?.map( u => u?.id );

    commentAsync.setLoading()
    try {
      const resp = await props.createApi({...comment, mentions});
      commentAsync.setDone(resp?.data)
      return resp?.data;
    }
    catch(e: any) {
      const errorMsg = intl.formatMessage({ id: e.response?.data.message || e.message });
      notification.error( { message: "Failed to save comment. " + errorMsg });
      commentAsync.setFail(e.message);
    }

    return;
  }

  const updateComment = async (commentAsync:AsyncState<Comment>, comment:Comment | undefined) : Promise<Comment | undefined> => {
    if (!comment) return;

    const mentions = parseUserFromMentions(comment.text)?.map( u => u?.id );

    commentAsync.setLoading()
    try {
      const resp = await props.updateApi({ ...comment, mentions })

      commentAsync.setDone(resp?.data)
      return resp?.data;
    }
    catch(e: any) {
      const errorMsg = intl.formatMessage({ id: e.response?.data.message || e.message });
      notification.error( { message: "Failed to save comment. " + errorMsg });
      commentAsync.setFail(e.message);
    }

    return;
  }

  const handleSetHidden = async (comment:Comment | undefined, hidden:boolean):Promise<void> => {
    if (!comment) return;

    const updated = await updateComment( commentAsync, { ...comment, hidden } );
    if ( updated ) {
      props.onSave?.(updated);
    }
  }

  const handleCancel = async () => {
    props.onCancel();
  }
  const handleSave = async ():Promise<Comment | undefined> => {

    try {

      const values = await form.validateFields() as CommentForm;
      const comment = {
        ...values,
        documents: values.documents?.map( d => d.response ? d.response : d ) 
        .map( (d:UploadedDocumentAsset) => ({
          id: d.id,
          assetUri: d.assetUri,
          filename: d.name,
          contentType: d.type,
        }) ) 
      } as Comment;
      const savedComment = await updateComment( commentAsync, comment )
 
      if ( savedComment ) {
        props.onSave(savedComment);
      }
    }
    catch(validationErrors) {
      notification.error({message: "Please fix validation errors." });
    }

    return;
  }

  const handleAdd = async () => {
    try {

      const values = await form.validateFields() as CommentForm;
      const comment = {
        ...values,
        documents: values.documents?.map( d => d.response ? d.response : d )
        .map( (d:UploadedDocumentAsset) => ({
          id: d.id,
          assetUri: d.assetUri,
          filename: d.name,
          contentType: d.type,
        }) )
      } as Comment;

      const savedComment = await createComment( commentAsync, comment );
      if ( savedComment ) {
        props.onSave(savedComment);
      }
    }
    catch(validationErrors) {
      notification.error({message: "Please fix validation errors." });
    }

    return;
  }

  const uploadDocumentUrl = configurator.api.getUploadCommentDocumentUrl();

  const handleUploadChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    return newFileList;
  }
console.log("render", props.comment)
  return <Card style={{marginBottom: ".4rem"}}
               styles={{
                 body: {padding: ".4rem .4rem 1rem .4rem"}
               }} >
    <Form 
      initialValues={props.comment}
      form={form}
    >
      <Form.Item name="id" hidden={true}>
        <Input/>
      </Form.Item>

      <Form.Item name="topic" hidden={true}>
        <Input/>
      </Form.Item>

      <Form.Item name="text" style={{marginBottom: "0"}}>
        <Mentions 
          rows={2}
          placeholder="Type a comment here" 
          options={mentionUserMap ? Object.keys( mentionUserMap )?.map( k => ({value: k, label: mentionUserMap[k].name }) ) : []}
        />
      </Form.Item>

      <div style={{display: "flex", justifyContent: "space-between"}}>
        {/* small formatting fix */}
        {(!props.topicTags?.length && !props.comment?.tags?.length) && <div></div>}
        <Form.Item style={{marginBottom: "0"}}
          name="tags"
          hidden={(!props.topicTags?.length && !props.comment?.tags?.length)}
        >
          <TagPopoverInput options={props.topicTags?.map(t => ({ value: t, label: t }))} />
        </Form.Item>

        <Form.Item style={{marginBottom: "0"}}
          name="internal"
          valuePropName="checked"
        >
          <Checkbox>Internal</Checkbox>
        </Form.Item>
      </div>

      <Form.Item
        name="documents"
        getValueFromEvent={handleUploadChange}
        valuePropName="fileList"
        noStyle
      >
        <Upload
          action={uploadDocumentUrl}
          name="document"
          listType="picture-card"
          withCredentials={true}
          showUploadList={ {
            showDownloadIcon: false,
            downloadIcon: <DownloadOutlined />,
            showPreviewIcon: false,
            showRemoveIcon: true,
          }}
          isImageUrl={(f) => !!f.type?.startsWith("image")  }
        >
          <div style={{ marginTop: 16 }}>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>Upload Document</div>
          </div>
        </Upload>
      </Form.Item>

      <Row justify={"space-between"} gutter={8} style={{marginTop: "1rem"}}>
        <Col>
          {asComment(props.comment)?.hidden
            ?<Button size="small" onClick={() => handleSetHidden(asComment(props.comment), false)}>Show</Button>
            :<Button size="small" onClick={() => handleSetHidden(asComment(props.comment), true)}>Hide</Button>}
        </Col>
        <Col>
          {(props.comment?.topic !== CommentTopic.SystemActivity) && <>
            {!isNewComment(props.comment)
              ? <Space>
                <Button size="small" onClick={handleCancel}>Cancel</Button>
                <Button size="small" onClick={handleSave} type="primary">Save</Button>
              </Space>
              : <Space>
                <Button size="small" onClick={handleCancel}>Cancel</Button>
                <Button size="small" onClick={handleAdd} type="primary">Add</Button>
              </Space> }
          </>}
        </Col>
      </Row>
    </Form>
  </Card>
}

const TagPopoverInput = (props:SelectProps<string[]>) => {

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const inputRef = useRef<any>(null);

  const handleOpen = () => {
    setIsOpen(true);
  }

  const handleBlur = () => {

    setIsOpen(false);
  }

  return <Popover
    trigger={"click"}
    open={isOpen}
    afterOpenChange={(open) => {
      if( open ) {
        inputRef.current?.focus();
      }
    }}
    content={<Select mode="multiple"
      ref={inputRef}
      style={{ width: "20rem"}}
      {...props}
      onBlur={handleBlur}
    /> }
    arrow={false}
  >
    <Button shape="circle" type="primary" size="small" style={{ height: "1rem", lineHeight: "0", minWidth: "1rem", marginRight: "1px"}} onClick={handleOpen} title="Tag this comment" >+</Button>
    {props.value &&
    props.value.map( t => <Button shape="round" type="primary" size="small" style={{ height: "1rem", lineHeight: "0", padding: "0 .4rem 0 .4rem", marginRight: "1px"}} onClick={handleOpen} >{t}</Button> )
    }
  </Popover>;
}


export default CommentCard;

